import React, { useMemo } from 'react';
import {
  Box, Typography, Alert, Chip, Stack, List, ListItem, ListItemIcon, ListItemText,
} from '@mui/material';
import {
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  CheckCircle as SuccessIcon,
  PlayArrow as StartIcon,
  Stop as EndIcon,
  AccountTree as NodeIcon,
  Timeline as EdgeIcon,
  Settings as ConfigIcon,
} from '@mui/icons-material';

// Validation rule types
export const VALIDATION_TYPES = {
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info',
  SUCCESS: 'success',
};

// Validation categories
export const VALIDATION_CATEGORIES = {
  CONNECTIVITY: 'connectivity',
  CONFIGURATION: 'configuration',
  LOGIC: 'logic',
  PERFORMANCE: 'performance',
  BEST_PRACTICES: 'best_practices',
};

// Validation rules
const VALIDATION_RULES = {
  // Connectivity rules
  hasStartNode: {
    type: VALIDATION_TYPES.INFO,
    category: VALIDATION_CATEGORIES.CONNECTIVITY,
    message: 'Consider adding a start node for clarity',
    validate: (nodes) => {
      const startNodes = nodes.filter((node) => node.type === 'start');
      return {
        isValid: startNodes.length > 0,
        details: startNodes.length === 0 ? 'No start node found (optional)' : `${startNodes.length} start node(s) found`,
        nodes: startNodes,
      };
    },
  },

  hasEndNode: {
    type: VALIDATION_TYPES.INFO,
    category: VALIDATION_CATEGORIES.CONNECTIVITY,
    message: 'Consider adding an end node for clarity',
    validate: (nodes) => {
      const endNodes = nodes.filter((node) => node.type === 'end');
      return {
        isValid: endNodes.length > 0,
        details: endNodes.length === 0 ? 'No end node found (optional)' : `${endNodes.length} end node(s) found`,
        nodes: endNodes,
      };
    },
  },

  noOrphanedNodes: {
    type: VALIDATION_TYPES.WARNING,
    category: VALIDATION_CATEGORIES.CONNECTIVITY,
    message: 'Some nodes are not connected',
    validate: (nodes, edges) => {
      const connectedNodeIds = new Set();
      edges.forEach((edge) => {
        connectedNodeIds.add(edge.source);
        connectedNodeIds.add(edge.target);
      });

      const orphanedNodes = nodes.filter((node) => !['start', 'end'].includes(node.type) && !connectedNodeIds.has(node.id));

      return {
        isValid: orphanedNodes.length === 0,
        details: orphanedNodes.length === 0 ? 'All nodes are connected' : `${orphanedNodes.length} unconnected node(s)`,
        nodes: orphanedNodes,
      };
    },
  },

  noCircularDependencies: {
    type: VALIDATION_TYPES.ERROR,
    category: VALIDATION_CATEGORIES.LOGIC,
    message: 'Flow should not have circular dependencies',
    validate: (nodes, edges) => {
      // Simple cycle detection using DFS
      const visited = new Set();
      const recursionStack = new Set();
      const cycles = [];

      const hasCycle = (nodeId, path = []) => {
        if (recursionStack.has(nodeId)) {
          cycles.push([...path, nodeId]);
          return true;
        }

        if (visited.has(nodeId)) {
          return false;
        }

        visited.add(nodeId);
        recursionStack.add(nodeId);
        path.push(nodeId);

        const outgoingEdges = edges.filter((edge) => edge.source === nodeId);
        let foundCycle = false;
        for (const edge of outgoingEdges) {
          if (hasCycle(edge.target, [...path])) {
            foundCycle = true;
          }
        }

        recursionStack.delete(nodeId);
        return foundCycle;
      };

      let hasAnyCycle = false;
      for (const node of nodes) {
        if (!visited.has(node.id)) {
          if (hasCycle(node.id)) {
            hasAnyCycle = true;
          }
        }
      }

      return {
        isValid: !hasAnyCycle,
        details: !hasAnyCycle ? 'No circular dependencies found' : `${cycles.length} circular dependency(ies) found`,
        cycles,
      };
    },
  },

  // Configuration rules
  aiAgentHasPrompt: {
    type: VALIDATION_TYPES.WARNING,
    category: VALIDATION_CATEGORIES.CONFIGURATION,
    message: 'AI Agent nodes should have a system prompt',
    validate: (nodes) => {
      const aiAgents = nodes.filter((node) => node.type === 'ai-agent');
      const invalidAgents = aiAgents.filter((agent) => !agent.data?.config?.systemPrompt || agent.data.config.systemPrompt.trim() === '');

      return {
        isValid: invalidAgents.length === 0,
        details: invalidAgents.length === 0 ? 'All AI agents have prompts' : `${invalidAgents.length} AI agent(s) missing prompts`,
        nodes: invalidAgents,
      };
    },
  },

  apiCallHasUrl: {
    type: VALIDATION_TYPES.WARNING,
    category: VALIDATION_CATEGORIES.CONFIGURATION,
    message: 'API Call nodes should have a valid URL',
    validate: (nodes) => {
      const apiCalls = nodes.filter((node) => node.type === 'api-call');
      const invalidApiCalls = apiCalls.filter((api) => !api.data?.config?.url || api.data.config.url.trim() === '');

      return {
        isValid: invalidApiCalls.length === 0,
        details: invalidApiCalls.length === 0 ? 'All API calls have URLs' : `${invalidApiCalls.length} API call(s) missing URLs`,
        nodes: invalidApiCalls,
      };
    },
  },

  codeExecutorHasCode: {
    type: VALIDATION_TYPES.WARNING,
    category: VALIDATION_CATEGORIES.CONFIGURATION,
    message: 'Code Executor nodes should have code',
    validate: (nodes) => {
      const codeExecutors = nodes.filter((node) => node.type === 'code-executor');
      const invalidExecutors = codeExecutors.filter((executor) => !executor.data?.config?.code || executor.data.config.code.trim() === '');

      return {
        isValid: invalidExecutors.length === 0,
        details: invalidExecutors.length === 0 ? 'All code executors have code' : `${invalidExecutors.length} code executor(s) missing code`,
        nodes: invalidExecutors,
      };
    },
  },

  webCrawlerHasUrls: {
    type: VALIDATION_TYPES.WARNING,
    category: VALIDATION_CATEGORIES.CONFIGURATION,
    message: 'Web Crawler nodes should have URLs or a URL variable',
    validate: (nodes) => {
      const crawlers = nodes.filter((node) => node.type === 'web-crawler');
      const invalid = crawlers.filter((n) => {
        const c = n.data?.config || {};
        const hasUrls = Array.isArray(c.urls) ? c.urls.length > 0 : false;
        const hasVar = (c.urlVariable || '').trim().length > 0;
        return !hasUrls && !hasVar;
      });
      return {
        isValid: invalid.length === 0,
        details: invalid.length === 0 ? 'All web crawlers have URLs' : `${invalid.length} web crawler(s) missing URLs or urlVariable`,
        nodes: invalid,
      };
    },
  },

  connectorHasConfig: {
    type: VALIDATION_TYPES.WARNING,
    category: VALIDATION_CATEGORIES.CONFIGURATION,
    message: 'Connector nodes should have connector, operation, and credential ref',
    validate: (nodes) => {
      const connectors = nodes.filter((node) => node.type === 'connector');
      const invalid = connectors.filter((n) => {
        const c = n.data?.config || {};
        return !(c.connectorId || '').trim() || !(c.operationId || '').trim() || !(c.credentialRef || '').trim();
      });
      return {
        isValid: invalid.length === 0,
        details: invalid.length === 0 ? 'All connectors configured' : `${invalid.length} connector(s) missing connectorId, operationId, or credentialRef`,
        nodes: invalid,
      };
    },
  },

  // Logic rules
  conditionalHasCondition: {
    type: VALIDATION_TYPES.WARNING,
    category: VALIDATION_CATEGORIES.LOGIC,
    message: 'Conditional nodes should have a condition',
    validate: (nodes) => {
      const conditionals = nodes.filter((node) => node.type === 'conditional');
      const invalidConditionals = conditionals.filter((conditional) => !conditional.data?.config?.condition || conditional.data.config.condition.trim() === '');

      return {
        isValid: invalidConditionals.length === 0,
        details: invalidConditionals.length === 0 ? 'All conditionals have conditions' : `${invalidConditionals.length} conditional(s) missing conditions`,
        nodes: invalidConditionals,
      };
    },
  },

  loopHasCondition: {
    type: VALIDATION_TYPES.WARNING,
    category: VALIDATION_CATEGORIES.LOGIC,
    message: 'Loop nodes should have a condition or array',
    validate: (nodes) => {
      const loops = nodes.filter((node) => node.type === 'loop');
      const invalidLoops = loops.filter((loop) => (!loop.data?.config?.condition || loop.data.config.condition.trim() === '')
        && (!loop.data?.config?.array || loop.data.config.array.trim() === ''));

      return {
        isValid: invalidLoops.length === 0,
        details: invalidLoops.length === 0 ? 'All loops have conditions' : `${invalidLoops.length} loop(s) missing conditions`,
        nodes: invalidLoops,
      };
    },
  },

  // Performance rules
  maxNodesPerFlow: {
    type: VALIDATION_TYPES.WARNING,
    category: VALIDATION_CATEGORIES.PERFORMANCE,
    message: 'Large flows may impact performance',
    validate: (nodes) => {
      const maxNodes = 50;
      const nodeCount = nodes.length;

      return {
        isValid: nodeCount <= maxNodes,
        details: nodeCount <= maxNodes ? `${nodeCount} nodes (good)` : `${nodeCount} nodes (consider breaking into smaller flows)`,
        nodeCount,
      };
    },
  },

  // Best practices
  hasDescriptiveLabels: {
    type: VALIDATION_TYPES.INFO,
    category: VALIDATION_CATEGORIES.BEST_PRACTICES,
    message: 'Nodes should have descriptive labels',
    validate: (nodes) => {
      const nodesWithoutLabels = nodes.filter((node) => !node.data?.label || node.data.label.trim() === '' || node.data.label === node.type);

      return {
        isValid: nodesWithoutLabels.length === 0,
        details: nodesWithoutLabels.length === 0 ? 'All nodes have descriptive labels' : `${nodesWithoutLabels.length} node(s) need better labels`,
        nodes: nodesWithoutLabels,
      };
    },
  },

  hasComments: {
    type: VALIDATION_TYPES.INFO,
    category: VALIDATION_CATEGORIES.BEST_PRACTICES,
    message: 'Complex flows should have comments',
    validate: (nodes) => {
      const complexNodes = nodes.filter((node) => ['ai-agent', 'code-executor', 'conditional', 'loop'].includes(node.type));

      return {
        isValid: complexNodes.length <= 5, // Simple heuristic
        details: complexNodes.length <= 5 ? 'Flow complexity is manageable' : 'Consider adding comments for complex flows',
        complexNodeCount: complexNodes.length,
      };
    },
  },
};

// Main validation function
export const validateFlow = (nodes = [], edges = [], globalState = {}) => {
  const results = [];

  Object.entries(VALIDATION_RULES).forEach(([ruleName, rule]) => {
    try {
      const result = rule.validate(nodes, edges, globalState);
      results.push({
        id: ruleName,
        type: rule.type,
        category: rule.category,
        message: rule.message,
        isValid: result.isValid,
        details: result.details,
        data: result,
      });
    } catch (error) {
      results.push({
        id: ruleName,
        type: VALIDATION_TYPES.ERROR,
        category: rule.category,
        message: `Validation error: ${rule.message}`,
        isValid: false,
        details: `Error: ${error.message}`,
        error,
      });
    }
  });

  return results;
};

// Validation summary component
export function ValidationSummary({ validationResults = [], onNodeClick = () => {} }) {
  const summary = useMemo(() => {
    const counts = {
      [VALIDATION_TYPES.ERROR]: 0,
      [VALIDATION_TYPES.WARNING]: 0,
      [VALIDATION_TYPES.INFO]: 0,
      [VALIDATION_TYPES.SUCCESS]: 0,
    };

    validationResults.forEach((result) => {
      counts[result.type]++;
    });

    return counts;
  }, [validationResults]);

  const getIcon = (type) => {
    switch (type) {
      case VALIDATION_TYPES.ERROR: return <ErrorIcon color="error" />;
      case VALIDATION_TYPES.WARNING: return <WarningIcon color="warning" />;
      case VALIDATION_TYPES.INFO: return <InfoIcon color="info" />;
      case VALIDATION_TYPES.SUCCESS: return <SuccessIcon color="success" />;
      default: return <InfoIcon />;
    }
  };

  const getColor = (type) => {
    switch (type) {
      case VALIDATION_TYPES.ERROR: return 'error';
      case VALIDATION_TYPES.WARNING: return 'warning';
      case VALIDATION_TYPES.INFO: return 'info';
      case VALIDATION_TYPES.SUCCESS: return 'success';
      default: return 'default';
    }
  };

  if (validationResults.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No validation results available
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {/* Summary Header */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Flow Validation
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          {Object.entries(summary).map(([type, count]) => (
            <Chip
              key={type}
              icon={getIcon(type)}
              label={`${count} ${type}s`}
              color={getColor(type)}
              size="small"
              variant={count > 0 ? 'filled' : 'outlined'}
            />
          ))}
        </Stack>
      </Box>

      {/* Validation Results */}
      <List dense>
        {validationResults.map((result, index) => (
          <ListItem
            key={result.id || index}
            sx={{
              borderLeft: 3,
              borderLeftColor: `${getColor(result.isValid ? VALIDATION_TYPES.SUCCESS : result.type)}.main`,
              mb: 1,
              bgcolor: result.isValid ? 'success.lighter' : 'error.lighter',
              borderRadius: 1,
            }}
          >
            <ListItemIcon>
              {getIcon(result.isValid ? VALIDATION_TYPES.SUCCESS : result.type)}
            </ListItemIcon>
            <ListItemText
              primary={result.message}
              secondary={result.details}
              primaryTypographyProps={{
                variant: 'body2',
                fontWeight: result.isValid ? 400 : 600,
              }}
              secondaryTypographyProps={{
                variant: 'caption',
                color: 'text.secondary',
              }}
            />
            {result.data?.nodes && result.data.nodes.length > 0 && (
              <Box sx={{ ml: 1 }}>
                <Chip
                  label={`${result.data.nodes.length} node(s)`}
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    // Highlight nodes in the flow
                    result.data.nodes.forEach((node) => onNodeClick(node));
                  }}
                />
              </Box>
            )}
          </ListItem>
        ))}
      </List>
    </Box>
  );
}

// Hook for real-time validation
export const useFlowValidation = (nodes, edges, globalState) => useMemo(() => validateFlow(nodes, edges, globalState), [nodes, edges, globalState]);

export default {
  validateFlow,
  ValidationSummary,
  useFlowValidation,
  VALIDATION_TYPES,
  VALIDATION_CATEGORIES,
  VALIDATION_RULES,
};
